import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  HourglassBottom as HourglassIcon,
  LocalShipping as TruckIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  CheckCircle as CompleteIcon,
  Note as NoteIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  Phone as PhoneIcon,
  Directions as DirectionsIcon,
  Assignment as DetailsIcon,
  Close as CloseIcon,
  RateReview as ObservationIcon,
  NotificationImportant as ReminderIcon,
  Block as SkipIcon,
  HelpOutline as HelpIcon,
  Home as HomeIcon,
  FactCheck as InspectionIcon,
  Undo as ResetIcon,
  AcUnit as SnowIcon,
  WbSunny as SummerIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { canViewCustomerPhone, isSubcontractor } from '../../utils/roles';
import GeofenceStatusChip from '../Common/GeofenceStatusChip';
import LanguageSwitcher from '../LanguageSwitcher';
import SummerJobDetails from '../Jobs/SummerJobDetails';
import WinterJobDetails from '../Jobs/WinterJobDetails';
import ContractedServicesChecklist from '../Jobs/ContractedServicesChecklist';
import WinterAdditionalServices from '../Jobs/WinterAdditionalServices';
import JobPhotosSection from '../Jobs/JobPhotosSection';
import { buildContractedServiceItems } from '../../utils/contractedServiceItems';
import PropertyDiagramViewer from '../Common/PropertyDiagramViewer';
import TimeTrackingWidget from '../TimeTracking/TimeTrackingWidget';
import MapboxMiniMap from '../Maps/MapboxMiniMap';
import { reverseGeocodeShortCached } from '../../utils/geocoding';
import { crewSeatName } from '../../utils/truckCrewNames';

const BIG_TOUCH = { minHeight: 52, fontSize: '1rem', fontWeight: 600 };
const CREW_REFRESH_INTERVAL_MS = 60000;

const getLocalDateIso = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSkippedStop = (stop) => stop?.is_cancelled === true || stop?.is_cancelled === 'true';

// Managers' truck pick sticks indefinitely. A crew member's pick of another
// truck only lasts for the day it was made, so every morning they land back
// on their own truck.
const MANAGER_TRUCK_LS_KEY = 'crew.manualTruckId';
const CREW_TRUCK_LS_KEY = 'crew.viewTruck';

// TKT-443: Summer / Snow switch. Snow shows the truck's season-long snow
// list (PL Hub's Winter Logs). The pick lasts for the day it was made.
const ROUTE_MODE_LS_KEY = 'crew.routeMode';

const readStoredRouteMode = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(ROUTE_MODE_LS_KEY) || 'null');
    return ['summer', 'snow'].includes(stored?.mode) ? stored : null;
  } catch {
    return null;
  }
};

const readStoredTruckPick = (fleetWide) => {
  try {
    if (fleetWide) {
      const stored = localStorage.getItem(MANAGER_TRUCK_LS_KEY);
      return stored ? { truckId: parseInt(stored, 10), date: null } : null;
    }
    const stored = JSON.parse(localStorage.getItem(CREW_TRUCK_LS_KEY) || 'null');
    return stored?.truckId ? stored : null;
  } catch {
    return null;
  }
};

const getStopIdentity = (stop) => {
  const customerTruckKey = [
    stop?.customer_id,
    stop?.truck_id,
  ].filter(Boolean).join('|');
  if (customerTruckKey) return `customer-truck-${customerTruckKey}`;

  const addressKey = [
    stop?.customer_id,
    stop?.truck_id,
    stop?.street_address,
    stop?.city,
  ].filter(Boolean).join('|');
  if (addressKey) return `address-${addressKey}`;

  if (stop?.id != null) return `site-${stop.id}`;
  if (stop?.active_job_id != null) return `job-${stop.active_job_id}`;
  return '';
};

const uniqueCrewStops = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const key = getStopIdentity(item);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Mobile-first landing page for field crews.
 *
 * Shows today's stops for the crew's assigned truck, in priority order.
 * Each card has clock-in / clock-out / mark-complete controls with big
 * touch targets and a geofence status chip. No CRM, quotes, admin, etc.
 */
const CrewToday = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const canSelectAnyTruck = ['crew_manager', 'sales', 'admin', 'superadmin']
    .includes(user?.user_type);
  // TKT-305: labourers/crew see address only. Crew managers keep the number.
  const showCustomerPhone = canViewCustomerPhone(user);
  const showEmployeeTimeClock = ['employee', 'crew_manager'].includes(user?.user_type);
  // TKT-461: subcontractors only have Crew Mode for their own trucks. They
  // don't do inspections in QERB or track their own time, and have no
  // dashboard to go home to.
  const subcontractor = isSubcontractor(user);
  // Admins can see a truck's completed stops and put a mistaken clock-in /
  // clock-out back to not started.
  const isAdmin = ['admin', 'superadmin'].includes(user?.user_type);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stops, setStops] = useState([]);
  // Per Naomi (TKT-176) completed properties should not stay in the active
  // crew list — they cause confusion about what's still to do. We hide
  // them here but track the count so the crew can still see progress.
  const [completedToday, setCompletedToday] = useState(0);
  const [completedStops, setCompletedStops] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [truckInfo, setTruckInfo] = useState(null);
  const [ownTruck, setOwnTruck] = useState(null);
  const [allTrucks, setAllTrucks] = useState([]);
  const [selectedTruckGps, setSelectedTruckGps] = useState(null);
  const [selectedTruckAddress, setSelectedTruckAddress] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  // Crew on more than one truck (office-assigned additional crew) can switch
  // between those trucks; their main truck is the default.
  const [truckPick, setTruckPick] = useState(() => readStoredTruckPick(canSelectAnyTruck));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // TKT-242 follow-up: when the auth token has expired, the data load 401s.
  // Instead of the blunt "could not load trucks" red banner, show a friendly
  // "session expired, please sign in again" screen.
  const [sessionExpired, setSessionExpired] = useState(false);

  // Crew help dialog
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpText, setHelpText] = useState('');
  const [submittingHelp, setSubmittingHelp] = useState(false);

  // Note dialog
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteTarget, setNoteTarget] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Complete dialog (confirmation with optional completion note +
  // TKT-195 services checklist promoted into the clock-out flow).
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [completeNote, setCompleteNote] = useState('');
  const [completing, setCompleting] = useState(false);
  const [completeServices, setCompleteServices] = useState({});

  // TKT-242: Clock-In services checklist. The crew explicitly confirms the
  // services they intend to do at the start of the visit.
  const [clockInOpen, setClockInOpen] = useState(false);
  const [clockInTarget, setClockInTarget] = useState(null);
  const [clockInServices, setClockInServices] = useState({});
  const [winterLogJob, setWinterLogJob] = useState(null);
  const [winterLogLoading, setWinterLogLoading] = useState(false);
  // TKT-486: Site Check / Salt Bin Refill ticked in the Clock In / Clock Out box.
  const [winterExtras, setWinterExtras] = useState([]);

  // Pending clock action (job_id -> 'in' | 'out')
  const [clocking, setClocking] = useState({});

  // Job-details dialog (full summer/winter service checkboxes + observations)
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailJob, setDetailJob] = useState(null);
  const [detailAllotment, setDetailAllotment] = useState(null);
  const [obsTemplates, setObsTemplates] = useState([]);
  const [obsDraft, setObsDraft] = useState({ templateId: '', note: '' });
  const [savingObs, setSavingObs] = useState(false);

  // FB-004: pre-clock-in urgent-reminder confirmation dialog. Gates Clock In
  // when the office set an urgent reminder on the property and this crew
  // member hasn't acknowledged it yet today. Mirrors the legacy
  // chkUrgentReminder modal pattern.
  const [reminderConfirmOpen, setReminderConfirmOpen] = useState(false);
  const [reminderConfirmStop, setReminderConfirmStop] = useState(null);
  const [reminderAck, setReminderAck] = useState(false);
  const [acknowledgingReminder, setAcknowledgingReminder] = useState(false);

  // FB-004: read-only viewer for the urgent reminder. Opened from the bell
  // icon button on the stop card so the crew can re-read the popup at any
  // time — useful if they dismissed the gating modal without reading it,
  // or just want to double-check a code mid-job.
  const [reminderViewerOpen, setReminderViewerOpen] = useState(false);
  const [reminderViewerStop, setReminderViewerStop] = useState(null);

  // Tick once a minute so the "X min remaining / X min over" chips on
  // currently-clocked-in stops stay live without a full reload.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const locale = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en-US';
  const todayIso = getLocalDateIso(currentDate);
  const manualTruckId = truckPick && (canSelectAnyTruck || truckPick.date === todayIso)
    ? truckPick.truckId
    : null;
  const [routeModePick, setRouteModePick] = useState(readStoredRouteMode);
  const [routeMode, setRouteMode] = useState('summer');
  const [routeCounts, setRouteCounts] = useState({ summer: 0, snow: 0 });
  const todaysRouteModePick = routeModePick?.date === todayIso ? routeModePick.mode : null;

  // -----------------------------------------------------------------------
  // FB-005 / FB-004 helpers: allotted time + urgent reminder
  // -----------------------------------------------------------------------

  // Allotted minutes for a stop. The API computes allotted_minutes from the
  // legacy service-time rules; the remaining checks are fallbacks for older
  // payloads or jobs that have not been materialized yet.
  // Returns null when nothing is configured (no chip shown).
  const allottedMinutesFor = (stop) => {
    if (!stop) return null;
    const allotted = stop.allotted_minutes;
    if (allotted != null && allotted !== '' && Number(allotted) > 0) {
      return Number(allotted);
    }
    const custom = stop.custom_job_site_log_time;
    if (custom != null && custom !== '' && Number(custom) > 0) {
      return Number(custom);
    }
    const allocated = stop.allocated_minutes;
    if (allocated != null && allocated !== '' && Number(allocated) > 0) {
      return Number(allocated);
    }
    const lawn = Number(stop.lawn_work_time || 0);
    const garden = Number(stop.gardening_work_time || 0);
    const sum = lawn + garden;
    return sum > 0 ? sum : null;
  };

  // Format minutes as "Xh Ym" / "Y min" for compact chips.
  const formatMinutes = (mins) => {
    if (mins == null) return '';
    const m = Math.max(0, Math.round(mins));
    if (m < 60) return t('crew.minutesShort', { count: m });
    const hours = Math.floor(m / 60);
    const remainder = m % 60;
    if (remainder === 0) return t('crew.hoursShort', { count: hours });
    return t('crew.hoursMinutesShort', { hours, minutes: remainder });
  };

  const formatHoursMinutesClock = (mins) => {
    const m = Math.max(0, Math.round(Number(mins) || 0));
    const hours = Math.floor(m / 60);
    const minutes = m % 60;
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  };

  const formatAllottedTime = (mins) => {
    if (mins == null) return '';
    const m = Math.max(0, Math.round(mins));
    return t('crew.minutesWithHoursClock', {
      minutes: m,
      clock: formatHoursMinutesClock(m),
      time: formatMinutes(m),
    });
  };

  // TKT-419: the allotted total broken out per service ("Lawn
  // Maintenance: 60 min"), from allotted_breakdown on the /daily stop.
  const allottedBreakdownLabel = (item) => {
    const key = item?.key || item?.service_type || '';
    const breakdownKey = `routeSetup.breakdownLabels.${key}`;
    const translated = t(breakdownKey);
    if (translated !== breakdownKey) return translated;
    const serviceKey = `serviceNames.${key}`;
    const service = t(serviceKey);
    return service !== serviceKey ? service : key;
  };

  const renderAllottedBreakdown = (source) => {
    const items = Array.isArray(source?.allotted_breakdown) ? source.allotted_breakdown : [];
    if (items.length === 0) return null;
    return (
      <Box sx={{ mt: 1 }} data-demo-target="crew-allotted-breakdown">
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {t('crew.allottedBreakdownTitle')}
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {items.map((item, idx) => (
            <Typography component="li" variant="body2" key={`${item.key || item.service_type}-${idx}`}>
              {t('crew.allottedBreakdownItem', {
                service: allottedBreakdownLabel(item),
                minutes: Math.round(Number(item.minutes) || 0),
              })}
            </Typography>
          ))}
        </Box>
        {source.allotted_source === 'adjusted' && (
          <Typography variant="caption" color="text.secondary" display="block">
            {t('crew.allottedBreakdownCustom')}
          </Typography>
        )}
      </Box>
    );
  };

  const formatClockTime = (date) =>
    date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });

  const hasUrgentReminder = (stop) =>
    !!(stop && typeof stop.urgent_reminder === 'string'
       && stop.urgent_reminder.trim() !== '');

  // TKT-282: office can save an optional Spanish translation alongside the
  // English original (urgent_reminder_es). Show crews the reminder in their
  // own selected language, falling back to English when no Spanish version
  // has been saved - never machine-translate at display time.
  const getReminderText = (stop) => {
    if (!stop) return '';
    const isSpanish = (i18n.language || '').toLowerCase().startsWith('es');
    if (isSpanish && typeof stop.urgent_reminder_es === 'string' && stop.urgent_reminder_es.trim() !== '') {
      return stop.urgent_reminder_es;
    }
    return stop.urgent_reminder || '';
  };

  // -----------------------------------------------------------------------
  // Data load: find the user's truck, then today's job_sites for it.
  // -----------------------------------------------------------------------

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError('');
    try {
      // Managers get every active truck; regular crew only get the trucks
      // they are on (the API scopes this).
      const trucksRes = await makeApiCall('/api/routes/trucks');
      // TKT-242 follow-up: a 401 means the session lapsed, not a server fault.
      if (trucksRes.status === 401) { setSessionExpired(true); return; }
      if (!trucksRes.ok) throw new Error(t('crew.failedToLoadTrucks'));
      const trucksData = await trucksRes.json();
      const trucks = (trucksData.trucks || []).filter((tr) => tr.is_active !== false);
      setAllTrucks(trucks);

      // Resolve which truck's schedule to show. Two sources:
      //   (a) Main truck: the truck where the user is the current driver or
      //       passenger, OR (TKT-279) the truck an office admin assigned
      //       them to (users.assigned_truck_id). Same precedence as
      //       /api/routes/daily: the seat wins over a possibly stale
      //       office assignment. This is the default.
      //   (b) Manual pick: managers can pick any truck. Regular crew can
      //       only pick among the trucks the API returned for them (main
      //       truck plus any where the office added them as additional
      //       crew), and their pick expires at the end of the day.
      const autoAssigned = trucks.find((tr) => tr.current_driver_user_id === user?.id)
        || trucks.find((tr) => tr.current_passenger_user_id === user?.id)
        || trucks.find((tr) => user?.assigned_truck_id && tr.id === user.assigned_truck_id)
        || null;
      setOwnTruck(autoAssigned);
      const manualAssigned = manualTruckId
        ? trucks.find((tr) => tr.id === manualTruckId)
        : null;

      const effectiveTruck = manualAssigned || autoAssigned || trucks[0] || null;
      setTruckInfo(effectiveTruck);

      if (canSelectAnyTruck && effectiveTruck?.id) {
        setGpsLoading(true);
        try {
          const gpsResponse = await makeApiCall('/api/gps/locations/current?include_inactive=true');
          if (gpsResponse.ok) {
            const gpsData = await gpsResponse.json();
            const gps = (gpsData.truck_locations || [])
              .find((location) => String(location.truck_id) === String(effectiveTruck.id)) || null;
            setSelectedTruckGps(gps);
            if (gps?.latitude != null && gps?.longitude != null) {
              try {
                const address = await reverseGeocodeShortCached(gps.latitude, gps.longitude);
                setSelectedTruckAddress(address || '');
              } catch (geocodeError) {
                console.warn('Failed to geocode selected truck:', geocodeError);
                setSelectedTruckAddress(`${Number(gps.latitude).toFixed(5)}, ${Number(gps.longitude).toFixed(5)}`);
              }
            } else {
              setSelectedTruckAddress('');
            }
          } else {
            setSelectedTruckGps(null);
            setSelectedTruckAddress('');
          }
        } catch (gpsError) {
          console.warn('Failed to load selected truck GPS:', gpsError);
          setSelectedTruckGps(null);
          setSelectedTruckAddress('');
        } finally {
          setGpsLoading(false);
        }
      } else {
        setSelectedTruckGps(null);
        setSelectedTruckAddress('');
        setGpsLoading(false);
      }

      // Load today's schedule for the effective truck (or all trucks if none).
      const params = new URLSearchParams({ date: todayIso, hide_cancelled: 'true' });
      if (effectiveTruck?.id) params.append('truck_id', effectiveTruck.id);
      // TKT-443: the truck's season snow list comes alongside today's route,
      // so the Summer / Snow switch can show both counts.
      const snowParams = new URLSearchParams(params);
      snowParams.append('snow', 'true');
      const [dailyRes, snowRes] = await Promise.all([
        makeApiCall(`/api/routes/daily?${params.toString()}`),
        effectiveTruck?.id
          ? makeApiCall(`/api/routes/daily?${snowParams.toString()}`).catch(() => null)
          : Promise.resolve(null),
      ]);
      if (dailyRes.status === 401) { setSessionExpired(true); return; }
      if (!dailyRes.ok) throw new Error(t('crew.failedToLoadStops'));
      const dailyData = await dailyRes.json();
      const snowData = snowRes?.ok ? await snowRes.json().catch(() => ({})) : {};
      const summerItems = uniqueCrewStops(
        (dailyData.route_items || []).filter((it) => !isSkippedStop(it))
      );
      // TKT-467: snow-list sites never leave the list. A finished or skipped
      // visit just means Clock In starts the next one.
      const snowItems = uniqueCrewStops(snowData.route_items || [])
        .map((it) => ({ ...it, is_snow_list: true }));
      // Default to Summer, or to Snow for a truck that only has a snow list
      // (the Loader). A crew's own pick wins for the rest of the day.
      const mode = todaysRouteModePick
        || (summerItems.length === 0 && snowItems.length > 0 ? 'snow' : 'summer');
      setRouteMode(mode);
      setRouteCounts({
        summer: summerItems.filter((it) => it.is_complete !== true).length,
        snow: snowItems.length,
      });
      const isSnowMode = mode === 'snow';
      const rawItems = isSnowMode ? snowItems : summerItems;
      // Pull completed stops out of the visible list (Naomi/TKT-176). We
      // still surface the running tally beside the "Today's stops" header
      // so the crew can see how many properties they've finished. Snow
      // sites stay listed and count every finished visit.
      const completedCount = isSnowMode
        ? rawItems.reduce((sum, it) => sum + Number(it.completed_visits_today || 0), 0)
        : rawItems.filter((it) => it.is_complete === true).length;
      setCompletedToday(completedCount);
      setCompletedStops(effectiveTruck && !isSnowMode ? rawItems.filter((it) => it.is_complete === true) : []);
      let items = isSnowMode ? rawItems : rawItems.filter((it) => it.is_complete !== true);

      // Lazily ensure active_jobs exist for every stop so Clock In, Add Note,
      // and Services & Logs work even if the overnight scheduler never ran.
      // Done in parallel; failures are non-fatal (the specific stop will
      // stay without an active_job_id and its action buttons disabled).
      // Snow visits are only created at Clock In (startSnowVisit).
      if (effectiveTruck && items.length > 0 && !isSnowMode) {
        const ensureResults = await Promise.all(items.map(async (it) => {
          if (it.active_job_id || !it.id) return it;
          try {
            const r = await makeApiCall('/api/jobs/ensure-for-job-site', {
              method: 'POST',
              body: JSON.stringify({
                job_site_id: it.id,
                scheduled_date: todayIso,
              }),
            });
            if (!r.ok) return it;
            const d = await r.json();
            if (d.job?.id) {
              return {
                ...it,
                active_job_id: d.job.id,
                job_type: it.job_type || d.job.job_type,
              };
            }
          } catch (_) { /* non-fatal */ }
          return it;
        }));
        items = ensureResults;
      }

      setStops(effectiveTruck ? items : []);
    } catch (e) {
      console.error(e);
      setError(e.message || t('crew.failedToLoadStops'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    user?.id,
    user?.assigned_truck_id,
    canSelectAnyTruck,
    t,
    todayIso,
    manualTruckId,
    todaysRouteModePick,
  ]);

  const syncCurrentDateAndRefresh = useCallback(() => {
    const nextDate = new Date();
    const nextIso = getLocalDateIso(nextDate);

    if (nextIso !== todayIso) {
      setCurrentDate(nextDate);
      return;
    }

    loadData(true);
  }, [loadData, todayIso]);

  const handleManualTruckChange = (value) => {
    const id = value === '' || value == null ? null : parseInt(value, 10);
    if (canSelectAnyTruck) {
      setTruckPick(id == null ? null : { truckId: id, date: null });
      if (id == null) {
        localStorage.removeItem(MANAGER_TRUCK_LS_KEY);
      } else {
        localStorage.setItem(MANAGER_TRUCK_LS_KEY, String(id));
      }
      return;
    }
    // Picking your own truck just clears the override.
    if (id == null || id === ownTruck?.id) {
      setTruckPick(null);
      localStorage.removeItem(CREW_TRUCK_LS_KEY);
      return;
    }
    const pick = { truckId: id, date: todayIso };
    setTruckPick(pick);
    localStorage.setItem(CREW_TRUCK_LS_KEY, JSON.stringify(pick));
  };

  const handleRouteModeChange = (event, mode) => {
    if (!mode) return;
    const pick = { mode, date: todayIso };
    setRouteModePick(pick);
    try {
      localStorage.setItem(ROUTE_MODE_LS_KEY, JSON.stringify(pick));
    } catch { /* private mode: the pick still applies until reload */ }
  };

  const viewingOtherTruck = !!(ownTruck && truckInfo && truckInfo.id !== ownTruck.id);
  const needsInspection = !canSelectAnyTruck && !subcontractor
    && !!truckInfo && truckInfo.inspected_today === false;
  const showTruckPicker = canSelectAnyTruck || allTrucks.length > 1;

  // Own truck first, then the rest alphabetically (API already sorts by name).
  const pickerTrucks = ownTruck
    ? [ownTruck, ...allTrucks.filter((tr) => tr.id !== ownTruck.id)]
    : allTrucks;

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  // Auto-refresh every 60s so geofence chips, dispatch updates, and day
  // rollovers keep moving even when crew leaves the tab open all week.
  useEffect(() => {
    const id = setInterval(() => syncCurrentDateAndRefresh(), CREW_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [syncCurrentDateAndRefresh]);

  // Hidden mobile tabs can throttle timers heavily. Refresh immediately when
  // the browser wakes, the tab becomes visible, or the crew returns to it.
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        syncCurrentDateAndRefresh();
      }
    };

    window.addEventListener('focus', syncCurrentDateAndRefresh);
    window.addEventListener('pageshow', syncCurrentDateAndRefresh);
    document.addEventListener('visibilitychange', handleVisible);

    return () => {
      window.removeEventListener('focus', syncCurrentDateAndRefresh);
      window.removeEventListener('pageshow', syncCurrentDateAndRefresh);
      document.removeEventListener('visibilitychange', handleVisible);
    };
  }, [syncCurrentDateAndRefresh]);

  // -----------------------------------------------------------------------
  // Clock in / clock out
  // -----------------------------------------------------------------------

  const handleClock = async (stop, action, selectedServices = null, opts = {}) => {
    if (!stop.active_job_id) {
      setError(t('crew.noActiveJob'));
      return;
    }
    // FB-004: gate clock-in (not clock-out) on the office's urgent
    // reminder. Open a confirm modal first; the actual clock-in fires
    // only after the crew member ticks "I acknowledge" and the ack
    // POST succeeds. TKT-242: a fresh clock-in routes through the
    // Clock-In services dialog (openClockIn), which already handled the
    // gate, so it passes skipGate to avoid a double prompt.
    if (action === 'in' && !opts.skipGate && hasUrgentReminder(stop)
        && !stop.urgent_reminder_acknowledged) {
      setReminderConfirmStop(stop);
      setReminderAck(false);
      setReminderConfirmOpen(true);
      return;
    }
    setClocking((s) => ({ ...s, [stop.active_job_id]: action }));

    const call = async (coords = {}) => {
      const endpoint = action === 'in'
        ? `api/jobs/${stop.active_job_id}/clock-in`
        : `api/jobs/${stop.active_job_id}/clock-out`;
      // TKT-242: on clock-in, send the crew's intended-services selection so
      // the backend records it in checked_services for the clock-out pre-check.
      const payload = (action === 'in' && Array.isArray(selectedServices) && selectedServices.length > 0)
        ? { ...coords, selected_services: selectedServices }
        : coords;
      const res = await makeApiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Request failed');
      }
      const data = await res.json().catch(() => ({}));
      if (action === 'in') {
        setSuccess(data.resumed ? t('jobs.jobResumed') : t('jobs.clockedInSuccessfully'));
      } else {
        setSuccess(t('jobs.clockedOutSuccessfully'));
      }
      await loadData(true);
    };

    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              await call({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
            } catch (err) {
              setError(err.message);
            } finally {
              setClocking((s) => { const n = { ...s }; delete n[stop.active_job_id]; return n; });
            }
          },
          async () => {
            try { await call({}); } catch (err) { setError(err.message); }
            finally { setClocking((s) => { const n = { ...s }; delete n[stop.active_job_id]; return n; }); }
          },
          { timeout: 8000 }
        );
      } else {
        await call({});
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
      setClocking((s) => { const n = { ...s }; delete n[stop.active_job_id]; return n; });
    }
  };

  // -----------------------------------------------------------------------
  // Admin: reset a stop that was clocked in / out by mistake
  // -----------------------------------------------------------------------

  const confirmReset = async () => {
    if (!resetTarget?.active_job_id) return;
    setResetting(true);
    try {
      const res = await makeApiCall(`api/jobs/${resetTarget.active_job_id}/reset-clock`, {
        method: 'POST',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('crew.resetStopFailed'));
      }
      setResetTarget(null);
      setSuccess(t('crew.resetStopSuccess'));
      await loadData(true);
    } catch (err) {
      console.error(err);
      setError(err.message || t('crew.resetStopFailed'));
    } finally {
      setResetting(false);
    }
  };

  // -----------------------------------------------------------------------
  // FB-004: acknowledge urgent reminder, then continue with clock-in.
  // -----------------------------------------------------------------------

  const closeReminderConfirm = () => {
    setReminderConfirmOpen(false);
    setReminderConfirmStop(null);
    setReminderAck(false);
  };

  const openReminderViewer = (stop) => {
    if (!hasUrgentReminder(stop)) return;
    setReminderViewerStop(stop);
    setReminderViewerOpen(true);
  };

  const closeReminderViewer = () => {
    setReminderViewerOpen(false);
    setReminderViewerStop(null);
  };

  const confirmReminderAndClockIn = async () => {
    const stop = reminderConfirmStop;
    if (!stop?.active_job_id || !reminderAck) return;
    try {
      setAcknowledgingReminder(true);
      const res = await makeApiCall(
        `api/jobs/${stop.active_job_id}/acknowledge-reminder`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('crew.failedToAckReminder'));
      }
      // Local optimistic update so subsequent re-clicks during the same
      // page session don't re-prompt before the next refresh.
      setStops((prev) => prev.map((s) =>
        s.active_job_id === stop.active_job_id
          ? { ...s, urgent_reminder_acknowledged: true }
          : s
      ));
      const acknowledgedStop = { ...stop, urgent_reminder_acknowledged: true };
      closeReminderConfirm();
      // TKT-242: route through the Clock-In services checklist now that the
      // reminder is acknowledged (the gate inside openClockIn will pass).
      openClockIn(acknowledgedStop);
    } catch (err) {
      setError(err.message || t('crew.failedToAckReminder'));
    } finally {
      setAcknowledgingReminder(false);
    }
  };

  // -----------------------------------------------------------------------
  // Notes
  // -----------------------------------------------------------------------

  const openNote = (stop) => {
    setNoteTarget(stop);
    setNoteText('');
    setNoteOpen(true);
  };

  const saveNote = async () => {
    if (!noteTarget?.active_job_id || !noteText.trim()) {
      setNoteOpen(false);
      return;
    }
    try {
      setSavingNote(true);
      const res = await makeApiCall(`api/jobs/${noteTarget.active_job_id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ message_content: noteText.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || t('crew.failedToSaveNote'));
      }
      setSuccess(t('crew.noteSaved'));
      setNoteOpen(false);
    } catch (err) {
      setError(err.message || t('crew.failedToSaveNote'));
    } finally {
      setSavingNote(false);
    }
  };

  // -----------------------------------------------------------------------
  // Crew help request
  // -----------------------------------------------------------------------

  const openHelp = () => {
    setHelpText('');
    setHelpOpen(true);
  };

  const closeHelp = () => {
    if (submittingHelp) return;
    setHelpOpen(false);
    setHelpText('');
  };

  const submitHelpRequest = async () => {
    const issue = helpText.trim();
    if (!issue) return;

    try {
      setSubmittingHelp(true);
      const response = await makeApiCall('api/tickets/crew-help', {
        method: 'POST',
        body: JSON.stringify({
          issue,
          truck_id: truckInfo?.id || manualTruckId || null,
          truck_name: truckInfo?.name || '',
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || t('crew.failedToSubmitHelp'));
      }

      setSuccess(t('crew.helpSubmitted', { ticketId: data.ticket?.id || '' }));
      setHelpOpen(false);
      setHelpText('');
    } catch (err) {
      setError(err.message || t('crew.failedToSubmitHelp'));
    } finally {
      setSubmittingHelp(false);
    }
  };

  // -----------------------------------------------------------------------
  // Complete (fires the Property Live Update email)
  // -----------------------------------------------------------------------

  // -----------------------------------------------------------------------
  // Job details dialog: services performed + observations
  // -----------------------------------------------------------------------

  const openJobDetails = async (stop) => {
    if (!stop.active_job_id) {
      setError(t('crew.noActiveJob'));
      return;
    }
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailJob(null);
    // /api/jobs/:id has no allotted_minutes, so keep the card's allotment
    // (and its per-service breakdown) for the dialog's time block.
    setDetailAllotment({
      allotted_minutes: stop.allotted_minutes,
      allotted_breakdown: stop.allotted_breakdown,
      allotted_source: stop.allotted_source,
    });
    setObsDraft({ templateId: '', note: '' });
    try {
      const res = await makeApiCall(`api/jobs/${stop.active_job_id}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load job');
      }
      const data = await res.json();
      const job = data.job || data; // endpoint may wrap or return directly
      setDetailJob(job);
      // Load observation templates for this job_type (best effort).
      try {
        const tplRes = await makeApiCall(
          `api/jobs/observation-templates?observation_type=${encodeURIComponent(job.job_type || 'summer')}`
        );
        if (tplRes.ok) {
          const tplData = await tplRes.json();
          // JS-019: the endpoint returns { observation_templates: [...] }.
          setObsTemplates(Array.isArray(tplData.observation_templates) ? tplData.observation_templates : []);
        }
      } catch (e) { /* non-fatal */ }
    } catch (err) {
      setError(err.message || t('crew.failedToLoadJob'));
      setDetailsOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeJobDetails = () => {
    setDetailsOpen(false);
    setDetailJob(null);
    setDetailAllotment(null);
    setObsDraft({ templateId: '', note: '' });
  };

  // Re-fetch the detail job (used by SummerJobDetails/WinterJobDetails after
  // they PATCH/PUT their pieces).
  const refreshDetailJob = async () => {
    if (!detailJob?.id) return;
    try {
      const res = await makeApiCall(`api/jobs/${detailJob.id}`);
      if (!res.ok) return;
      const data = await res.json();
      setDetailJob(data.job || data);
    } catch (err) {
      console.warn('refreshDetailJob failed', err);
    }
  };

  const saveObservation = async () => {
    if (!detailJob?.id) return;
    if (!obsDraft.templateId && !obsDraft.note.trim()) return;
    try {
      setSavingObs(true);
      const res = await makeApiCall(`api/jobs/${detailJob.id}/observations`, {
        method: 'POST',
        body: JSON.stringify({
          observation_template_id: obsDraft.templateId || null,
          custom_note: obsDraft.note.trim() || null,
          severity: 'normal',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save');
      }
      setSuccess(t('crew.observationSaved'));
      setObsDraft({ templateId: '', note: '' });
      await refreshDetailJob();
    } catch (err) {
      setError(err.message || t('crew.failedToSaveObservation'));
    } finally {
      setSavingObs(false);
    }
  };

  // TKT-304: same "Services Performed Today" list as the summer job log,
  // limited to services actually selected on the contract. The quote import
  // marked unused namespaces `enabled: true`, so we cannot trust that flag.
  // Winter visits list the contract's snow work instead.
  const buildCrewServiceItems = (stop) => buildContractedServiceItems(
    stop?.available_services,
    { t, customerType: stop?.customer_type, jobType: stop?.job_type }
  );

  // TKT-424: winter visits fill in the winter log (services by area,
  // pavement, weather, salt) inside Clock In and Clock Out instead of a trip
  // through Services & Logs. The log saves to the visit as the crew types.
  const isWinterStop = (stop) => stop?.job_type === 'winter';

  const loadWinterLog = async (jobId) => {
    setWinterLogJob(null);
    setWinterExtras([]);
    setWinterLogLoading(true);
    try {
      const res = await makeApiCall(`api/jobs/${jobId}`);
      if (!res.ok) throw new Error(t('crew.failedToLoadWinterLog'));
      const data = await res.json();
      const job = data.job || data;
      setWinterLogJob(job);
      setWinterExtras(job?.snow_services?.additional_services || []);
    } catch (err) {
      setError(err.message || t('crew.failedToLoadWinterLog'));
    } finally {
      setWinterLogLoading(false);
    }
  };

  // TKT-486: Site Check / Salt Bin Refill sit with the other services in the
  // first box and save to the winter log straight away. Only this list is
  // sent, so the area checkboxes in the log below are left as they are.
  const saveWinterExtras = async (codes) => {
    const jobId = winterLogJob?.id;
    if (!jobId) return;
    const previous = winterExtras;
    setWinterExtras(codes);
    try {
      const res = await makeApiCall(`api/jobs/${jobId}/snow-services`, {
        method: 'PUT',
        body: JSON.stringify({ additional_services: codes }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || t('winterLogs.saveFailedGeneric'));
      }
    } catch (err) {
      setWinterExtras(previous);
      setError(err.message || t('winterLogs.saveFailedGeneric'));
    }
  };

  // `withTitle` when the contract list above is empty and showed no heading.
  const renderWinterExtras = (withTitle) => (
    <>
      {withTitle && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          {t('summerLogs.servicesPerformed')}
        </Typography>
      )}
      <WinterAdditionalServices
        value={winterExtras}
        onChange={saveWinterExtras}
        disabled={winterLogLoading || !winterLogJob}
      />
    </>
  );

  // Clock In / Clock Out need at least one service ticked (or, at Clock Out,
  // a note). On a winter visit Site Check or Salt Bin Refill counts too.
  const hasTickedService = (stop, values) => Object.values(values).some(Boolean)
    || (isWinterStop(stop) && winterExtras.length > 0);

  const renderWinterLog = () => (
    <Box sx={{ mt: 1 }} data-demo-target="crew-winter-log">
      {winterLogLoading || !winterLogJob ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} aria-label={t('crew.loadingWinterLog')} />
        </Box>
      ) : (
        <WinterJobDetails key={winterLogJob.id} job={winterLogJob} onUpdate={() => {}} hideAdditionalServices />
      )}
    </Box>
  );

  // TKT-467: Clock In on a snow-list site starts a visit on the spot: the
  // open one if there is one, otherwise the next visit of the day.
  const startSnowVisit = async (stop) => {
    if (!stop?.id) return;
    const key = `site-${stop.id}`;
    setClocking((s) => ({ ...s, [key]: 'in' }));
    try {
      const res = await makeApiCall('/api/jobs/ensure-for-job-site', {
        method: 'POST',
        body: JSON.stringify({ job_site_id: stop.id, scheduled_date: todayIso, new_visit: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.job?.id) throw new Error(data.error || t('crew.failedToStartVisit'));
      const job = data.job;
      // Someone on the truck already clocked in on this visit: show it.
      if (job.clock_in_time && !job.clock_out_time) {
        await loadData(true);
        return;
      }
      openClockIn({
        ...stop,
        active_job_id: job.id,
        job_type: job.job_type || 'winter',
        available_services: job.available_services ?? stop.available_services,
        checked_services: job.checked_services,
        is_complete: !!job.is_complete,
        is_cancelled: !!job.is_cancelled,
        clock_in_time: job.clock_in_time,
        clock_out_time: job.clock_out_time,
        urgent_reminder_acknowledged: false,
      });
    } catch (err) {
      setError(err.message || t('crew.failedToStartVisit'));
    } finally {
      setClocking((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  // TKT-242: open the Clock-In services checklist. The crew confirms what they
  // intend to do before starting. Start blank so the crew must physically
  // confirm each service instead of accepting a pre-filled log.
  const openClockIn = (stop) => {
    if (!stop.active_job_id) {
      setError(t('crew.noActiveJob'));
      return;
    }
    // FB-004 reminder gate first — same behaviour as the old direct clock-in.
    if (hasUrgentReminder(stop) && !stop.urgent_reminder_acknowledged) {
      setReminderConfirmStop(stop);
      setReminderAck(false);
      setReminderConfirmOpen(true);
      return;
    }
    const items = buildCrewServiceItems(stop);
    // Nothing configured to check off -> clock in directly, no dialog.
    // Winter always opens it for the winter log.
    if (items.length === 0 && !isWinterStop(stop)) {
      handleClock(stop, 'in', null, { skipGate: true });
      return;
    }
    const initial = {};
    for (const item of items) {
      initial[item.key] = false;
    }
    setClockInServices(initial);
    setClockInTarget(stop);
    if (isWinterStop(stop)) loadWinterLog(stop.active_job_id);
    setClockInOpen(true);
  };

  const toggleClockInService = (key) => {
    setClockInServices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const confirmClockIn = async () => {
    const stop = clockInTarget;
    if (!stop?.active_job_id) return;
    const selectedKeys = Object.entries(clockInServices)
      .filter(([, checked]) => checked === true)
      .map(([key]) => key);
    setClockInOpen(false);
    await handleClock(stop, 'in', selectedKeys, { skipGate: true });
  };

  const clockInSelectionKeys = (raw) => {
    if (!raw) return new Set();
    let value = raw;
    if (typeof value === 'string') {
      try { value = JSON.parse(value); } catch { return new Set(); }
    }
    if (Array.isArray(value)) return new Set(value.map(String));
    if (value && typeof value === 'object') {
      return new Set(Object.entries(value).filter(([, checked]) => checked).map(([key]) => String(key)));
    }
    return new Set();
  };

  const openComplete = (stop) => {
    setCompleteTarget(stop);
    setCompleteNote('');
    // Naomi (18 Aug): services checked at clock-in start checked at clock-out.
    // The crew can still correct them before the completion log and email go out.
    const selectedAtClockIn = clockInSelectionKeys(stop?.checked_services);
    const initial = {};
    for (const item of buildCrewServiceItems(stop)) {
      initial[item.key] = selectedAtClockIn.has(item.key);
    }
    setCompleteServices(initial);
    // The winter log filled in at Clock In comes back for any last changes.
    if (isWinterStop(stop)) loadWinterLog(stop.active_job_id);
    setCompleteOpen(true);
  };

  const toggleCompleteService = (key) => {
    setCompleteServices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const confirmComplete = async () => {
    if (!completeTarget?.active_job_id) return;
    try {
      setCompleting(true);
      // TKT-195: send the service checklist alongside completion notes.
      // services_performed reaches the backend as the canonical record
      // of "what got done" for this visit. We only include keys the
      // crew actually checked; the backend uses [] as "no intent" so
      // passing an empty array would wipe existing data — we send
      // undefined in that case instead.
      const performedKeys = Object.entries(completeServices)
        .filter(([, checked]) => checked === true)
        .map(([key]) => key);
      const res = await makeApiCall(`api/jobs/${completeTarget.active_job_id}/complete-job`, {
        method: 'POST',
        body: JSON.stringify({
          completion_notes: completeNote.trim() || undefined,
          // Keep the array shape because complete-job's
          // didProvideServices guard checks Array.isArray + non-empty
          // length. We still send undefined when nothing was checked
          // so the backend preserves any prior selections made through
          // the deeper Services & Logs dialog.
          services_performed: performedKeys.length > 0 ? performedKeys : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('crew.failedToComplete'));
      setSuccess(t('crew.jobCompleted'));
      setCompleteOpen(false);
      await loadData(true);
    } catch (err) {
      setError(err.message || t('crew.failedToComplete'));
    } finally {
      setCompleting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  // TKT-242 follow-up: clear the expired token and bounce to the login screen.
  const handleSignInAgain = () => {
    try { logout(); } catch (_) { /* non-fatal */ }
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // TKT-242 follow-up: friendly session-expired screen instead of the blunt
  // "could not load trucks" red banner when the auth token has lapsed.
  if (sessionExpired) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Box
          sx={{
            p: 4,
            maxWidth: 420,
            width: '100%',
            textAlign: 'center',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 3,
          }}
        >
          <TimeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            {t('crew.sessionExpiredTitle', 'Your session has expired')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('crew.sessionExpiredMessage', 'You have been signed out. Please sign in again to keep using Crew Mode.')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handleSignInAgain}
            sx={BIG_TOUCH}
            aria-label={t('crew.signInAgain', 'Sign in again')}
          >
            {t('crew.signInAgain', 'Sign In Again')}
          </Button>
        </Box>
      </Box>
    );
  }

  const formatAddress = (s) => [s.street_address, s.city, s.province, s.postal_code].filter(Boolean).join(', ');

  const renderStopCard = (stop, idx) => {
    const title = stop.company_name
      || `${stop.first_name || ''} ${stop.last_name || ''}`.trim()
      || t('routeSetup.unknownCustomer');
    const addr = formatAddress(stop);
    const isCancelled = !!stop.is_cancelled;
    const isComplete = !!stop.is_complete;
    const hasBeenStarted = !!stop.clock_in_time;
    const isClockedIn = hasBeenStarted && !stop.clock_out_time;
    // True once the crew has clocked out at least once. Clock-in is still
    // allowed (crew resuming after lunch) and Mark Complete is unlocked
    // so a finished shift can wrap the job without needing another in/out.
    const isOnBreak = hasBeenStarted && !!stop.clock_out_time;
    const hasActiveJob = !!stop.active_job_id;
    // TKT-467: a snow-list site whose last visit is finished, skipped or not
    // started yet offers Clock In, which starts the next visit.
    const startsNewSnowVisit = !!stop.is_snow_list && (!hasActiveJob || isComplete || isCancelled);
    const snowVisitsDone = Number(stop.completed_visits_today || 0);
    const clockingState = startsNewSnowVisit
      ? clocking[`site-${stop.id}`]
      : clocking[stop.active_job_id];
    const showReminderBanner = hasUrgentReminder(stop)
      && !stop.urgent_reminder_acknowledged
      && !isComplete
      && !isCancelled;
    const cardAccent = isCancelled
      ? 'warning.main'
      : showReminderBanner
      ? 'error.main'
      : isComplete
        ? 'success.main'
        : isClockedIn ? 'warning.main' : 'primary.main';

    // FB-005: allotted-time pill set. We show:
    //   - "Allotted: 90 min" — always when a value is configured
    //   - "Expected complete: 2:45 PM" — once clocked in
    //   - "X min remaining" (success) or "X min over" (error) — live tick
    const allotted = allottedMinutesFor(stop);
    let expectedCompleteAt = null;
    let remainingMins = null;
    let isOver = false;
    if (allotted != null && stop.clock_in_time) {
      expectedCompleteAt = new Date(
        new Date(stop.clock_in_time).getTime() + allotted * 60 * 1000
      );
      // If the crew has clocked out (or marked complete), freeze the
      // remaining/over chip at clock-out instead of letting it keep ticking
      // — they're not on the clock anymore.
      const referenceTime = stop.clock_out_time
        ? new Date(stop.clock_out_time)
        : new Date();
      remainingMins = Math.round(
        (expectedCompleteAt.getTime() - referenceTime.getTime()) / 60000
      );
      isOver = remainingMins < 0;
    }

    return (
      <Card
        key={stop.id ?? `aj-${stop.active_job_id}`}
        variant="outlined"
        sx={{
          mb: 1.5,
          borderLeft: '6px solid',
          borderLeftColor: cardAccent,
          opacity: (isComplete || isCancelled) && !stop.is_snow_list ? 0.7 : 1,
        }}
        aria-label={t('crew.stopAria', { index: idx + 1, title })}
        data-demo-target="crew-stop-card"
      >
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Chip label={`#${idx + 1}`} color="primary" sx={{ fontWeight: 700 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {title}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {addr}
                </Typography>
              </Stack>
              {snowVisitsDone > 0 && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<CompleteIcon />}
                  label={t('crew.snowVisitsDone', { count: snowVisitsDone })}
                  sx={{ mt: 0.75 }}
                  data-demo-target="crew-snow-visits-done"
                />
              )}
              {showCustomerPhone && stop.phone_number && (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    <a
                      href={`tel:${stop.phone_number}`}
                      style={{ color: 'inherit' }}
                      aria-label={t('common.callCustomerAria', { phone: stop.phone_number })}
                    >
                      {stop.phone_number}
                    </a>
                  </Typography>
                </Stack>
              )}
            </Box>
            {/* FB-004: persistent re-read button. Visible whenever the office
                set an urgent reminder on this site (regardless of ack state)
                so the crew can re-open the popup any time — covers the case
                where they tapped through the gating modal too quickly. */}
            {hasUrgentReminder(stop) && (
              <IconButton
                onClick={() => openReminderViewer(stop)}
                color="error"
                size="large"
                aria-label={t('crew.viewReminderAria', { title })}
                data-demo-target="crew-btn-view-reminder"
                sx={{
                  flexShrink: 0,
                  border: '2px solid',
                  borderColor: 'error.main',
                  backgroundColor: stop.urgent_reminder_acknowledged
                    ? 'transparent'
                    : 'error.light',
                  color: stop.urgent_reminder_acknowledged
                    ? 'error.main'
                    : 'common.white',
                  '&:hover': {
                    backgroundColor: 'error.main',
                    color: 'common.white',
                  },
                }}
              >
                <ReminderIcon />
              </IconButton>
            )}
          </Stack>

          {isCancelled && !stop.is_snow_list && (
            <Alert
              severity="warning"
              icon={<SkipIcon fontSize="inherit" />}
              sx={{ mb: 1.5 }}
              data-demo-target="crew-stop-skipped"
              role="status"
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                {t('crew.stopSkippedTitle')}
              </AlertTitle>
              <Typography variant="body2">
                {stop.cancel_reason || t('crew.stopSkippedBody')}
              </Typography>
            </Alert>
          )}

          {showReminderBanner && (
            <Alert
              severity="error"
              icon={<ReminderIcon fontSize="inherit" />}
              sx={{ mb: 1.5, '& .MuiAlert-message': { width: '100%' } }}
              data-demo-target="crew-urgent-reminder-banner"
              role="alert"
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                {t('crew.urgentReminderTitle')}
              </AlertTitle>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                {getReminderText(stop)}
              </Typography>
              <Typography variant="caption" color="error.dark">
                {t('crew.urgentReminderHint')}
              </Typography>
            </Alert>
          )}
          {hasUrgentReminder(stop)
            && stop.urgent_reminder_acknowledged
            && !isComplete && (
            <Alert
              severity="warning"
              icon={<ReminderIcon fontSize="inherit" />}
              sx={{ mb: 1.5 }}
              data-demo-target="crew-urgent-reminder-acknowledged"
            >
              <AlertTitle sx={{ fontWeight: 700, mb: 0 }}>
                {t('crew.urgentReminderTitle')}
              </AlertTitle>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {getReminderText(stop)}
              </Typography>
            </Alert>
          )}

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <GeofenceStatusChip
              status={stop.gps_status}
              timeIn={stop.gps_time_in}
              timeOut={stop.gps_time_out}
              allottedMinutes={allottedMinutesFor(stop)}
              size="medium"
            />
            {stop.clock_in_time && (
              <Chip
                icon={<TimeIcon />}
                size="medium"
                label={
                  stop.clock_out_time
                    ? `${formatClockTime(new Date(stop.clock_in_time))} - ${formatClockTime(new Date(stop.clock_out_time))}`
                    : t('crew.clockedInAt', { time: formatClockTime(new Date(stop.clock_in_time)) })
                }
                color={stop.clock_out_time ? 'success' : 'warning'}
              />
            )}
            {isCancelled && (
              <Chip
                icon={<SkipIcon />}
                size="medium"
                color="warning"
                label={t('crew.stopSkippedChip')}
              />
            )}
            {/* FB-005: allotted time + live remaining/over indicator.
                Always show a chip so missing office setup is visible instead
                of silently hiding the feature. */}
            <Chip
              icon={<HourglassIcon />}
              size="medium"
              variant="outlined"
              color={allotted == null ? 'warning' : 'default'}
              label={
                allotted == null
                  ? t('crew.allottedNotSet')
                  : t('crew.allottedChip', { time: formatAllottedTime(allotted) })
              }
              aria-label={
                allotted == null
                  ? t('crew.allottedNotSetAria')
                  : t('crew.allottedAria', { time: formatAllottedTime(allotted) })
              }
              data-demo-target="crew-chip-allotted"
            />
            {expectedCompleteAt && !isComplete && (
              <Chip
                icon={<TimeIcon />}
                size="medium"
                variant="outlined"
                label={t('crew.expectedCompleteChip', {
                  time: formatClockTime(expectedCompleteAt),
                })}
                aria-label={t('crew.expectedCompleteAria', {
                  time: formatClockTime(expectedCompleteAt),
                })}
                data-demo-target="crew-chip-expected-complete"
              />
            )}
            {remainingMins != null && !isComplete && (
              <Chip
                size="medium"
                color={isOver ? 'error' : 'success'}
                label={
                  isOver
                    ? t('crew.minutesOver', { time: formatMinutes(Math.abs(remainingMins)) })
                    : t('crew.minutesRemaining', { time: formatMinutes(remainingMins) })
                }
                aria-label={
                  isOver
                    ? t('crew.minutesOver', { time: formatMinutes(Math.abs(remainingMins)) })
                    : t('crew.minutesRemaining', { time: formatMinutes(remainingMins) })
                }
                data-demo-target="crew-chip-remaining"
              />
            )}
          </Stack>
          {renderAllottedBreakdown(stop)}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="large"
              startIcon={<DirectionsIcon />}
              onClick={() => {
                const q = encodeURIComponent(addr);
                // TKT-196: Paramount trucks must never be routed onto the
                // 407 ETR or other toll roads. Google Maps honors the
                // `avoid=tolls,ferries` parameter so the suggested
                // directions skip those segments by default.
                window.open(
                  `https://www.google.com/maps/dir/?api=1&destination=${q}&travelmode=driving&avoid=tolls%2Cferries`,
                  '_blank',
                  'noopener'
                );
              }}
              sx={BIG_TOUCH}
              aria-label={t('crew.directions')}
              data-demo-target="crew-btn-directions"
            >
              {t('crew.directions')}
            </Button>
            {startsNewSnowVisit && (
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={clockingState === 'in' ? <CircularProgress size={18} color="inherit" /> : <StartIcon />}
                disabled={!!clockingState}
                onClick={() => startSnowVisit(stop)}
                sx={BIG_TOUCH}
                aria-label={snowVisitsDone > 0 ? t('crew.clockInAgain') : t('jobs.clockIn')}
                data-demo-target="crew-btn-clockin"
              >
                {snowVisitsDone > 0 ? t('crew.clockInAgain') : t('jobs.clockIn')}
              </Button>
            )}
            {!startsNewSnowVisit && !isComplete && !isCancelled && hasActiveJob && !isClockedIn && (
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={clockingState === 'in' ? <CircularProgress size={18} color="inherit" /> : <StartIcon />}
                disabled={!!clockingState}
                onClick={() => (isOnBreak ? handleClock(stop, 'in') : openClockIn(stop))}
                sx={BIG_TOUCH}
                aria-label={isOnBreak ? t('jobs.resumeJob') : t('jobs.clockIn')}
                data-demo-target={isOnBreak ? 'crew-btn-resume' : 'crew-btn-clockin'}
              >
                {isOnBreak ? t('jobs.resumeJob') : t('jobs.clockIn')}
              </Button>
            )}
            {/* TKT-177: clock-out IS the completion action now. Tapping
                Clock Out opens the same confirmation dialog the old "Mark
                Complete" button used; once confirmed the job is clocked
                out and marked complete in a single backend call. */}
            {!isComplete && !isCancelled && hasActiveJob && isClockedIn && (
              <Button
                variant="contained"
                color="warning"
                size="large"
                startIcon={<StopIcon />}
                disabled={!!clockingState}
                onClick={() => openComplete(stop)}
                sx={BIG_TOUCH}
                aria-label={t('jobs.clockOut')}
                data-demo-target="crew-btn-clockout"
              >
                {t('jobs.clockOut')}
              </Button>
            )}
            {/* "On break" rows are jobs that were clocked out under the
                legacy two-step flow but never marked complete. Surface a
                Complete control so the crew can finalize them without
                having to clock back in first. */}
            {!isComplete && !isCancelled && hasActiveJob && isOnBreak && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<CompleteIcon />}
                onClick={() => openComplete(stop)}
                sx={BIG_TOUCH}
                aria-label={t('crew.markComplete')}
                data-demo-target="crew-btn-complete"
              >
                {t('crew.markComplete')}
              </Button>
            )}
            <Button
              variant="outlined"
              size="large"
              startIcon={<NoteIcon />}
              onClick={() => openNote(stop)}
              disabled={!hasActiveJob || isCancelled}
              sx={BIG_TOUCH}
              aria-label={t('crew.addNote')}
              data-demo-target="crew-btn-note"
            >
              {t('crew.addNote')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<DetailsIcon />}
              onClick={() => openJobDetails(stop)}
              disabled={!hasActiveJob || isCancelled}
              sx={BIG_TOUCH}
              aria-label={t('crew.servicesAndLogs')}
              data-demo-target="crew-btn-details"
            >
              {t('crew.servicesAndLogs')}
            </Button>
            {isAdmin && !isCancelled && hasActiveJob && (hasBeenStarted || isComplete) && (
              <Button
                variant="outlined"
                color="error"
                size="large"
                startIcon={<ResetIcon />}
                disabled={!!clockingState}
                onClick={() => setResetTarget({ ...stop, title })}
                sx={BIG_TOUCH}
                aria-label={t('crew.resetStop')}
                data-demo-target="crew-btn-reset"
              >
                {t('crew.resetStop')}
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', pb: 10 }}>
      {/* Top bar */}
      <AppBar position="sticky" color="primary" elevation={1} data-demo-target="crew-topbar">
        <Toolbar sx={{ display: 'flex', gap: 1 }}>
          <TruckIcon />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {truckInfo?.name || t('crew.noTruckAssigned')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {currentDate.toLocaleDateString(locale, { weekday: 'long', month: 'short', day: 'numeric' })}
            </Typography>
          </Box>
          {/* Home opens the main dashboard. Login still lands crew on this
              screen; the dashboard shows them a button back to crew mode. */}
          {!subcontractor && (
            <IconButton
              color="inherit"
              onClick={() => navigate('/dashboard')}
              aria-label={t('common.goHome')}
              data-demo-target="crew-btn-home"
            >
              <HomeIcon />
            </IconButton>
          )}
          <Box data-demo-target="crew-language-switcher">
            <LanguageSwitcher variant="menu" />
          </Box>
          <IconButton
            color="inherit"
            onClick={openHelp}
            aria-label={t('crew.helpButton')}
            data-demo-target="crew-btn-help"
          >
            <HelpIcon />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={() => loadData(true)}
            disabled={refreshing}
            aria-label={t('common.refresh')}
          >
            {refreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          </IconButton>
          <IconButton color="inherit" onClick={logout} aria-label={t('common.logout')}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 1.5 }}>
        {showEmployeeTimeClock && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon color="primary" />
              {t('crew.employeeTimeClock')}
            </Typography>
            <TimeTrackingWidget compact />
          </Box>
        )}

        {/* Every staff member can do a vehicle inspection, whatever their
            department, position or job title. Subcontractors don't. */}
        {!subcontractor && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<InspectionIcon />}
            onClick={() => navigate('/dashboard/inspections/new')}
            sx={{ mb: 2 }}
          >
            {t('crew.startVehicleInspection')}
          </Button>
        )}

        {/* Driver / passenger line */}
        {truckInfo && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('jobs.driver')}:</strong> {crewSeatName(truckInfo, 'driver') || '-'}{'  \u00b7  '}
              <strong>{t('jobs.passenger')}:</strong> {crewSeatName(truckInfo, 'passenger') || '-'}
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
        {!truckInfo && !canSelectAnyTruck && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('crew.noTruckAssignedHelp')}
          </Alert>
        )}

        {showTruckPicker && (
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="crew-truck-picker-label">
                {t('crew.pickTruckLabel')}
              </InputLabel>
              <Select
                labelId="crew-truck-picker-label"
                value={truckInfo?.id ?? ''}
                label={t('crew.pickTruckLabel')}
                onChange={(e) => handleManualTruckChange(e.target.value)}
                inputProps={{ 'aria-label': t('crew.pickTruckLabel') }}
                renderValue={(value) => {
                  const tr = allTrucks.find((item) => item.id === value);
                  if (!tr) return '';
                  return tr.id === ownTruck?.id
                    ? t('crew.myTruckOption', { truck: tr.name })
                    : tr.name;
                }}
                data-demo-target="crew-truck-picker"
              >
                {pickerTrucks.map((tr) => (
                  <MenuItem
                    key={tr.id}
                    value={tr.id}
                    divider={tr.id === ownTruck?.id}
                    sx={{ minHeight: 48 }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                      sx={{ width: '100%' }}
                    >
                      <Typography sx={{ fontWeight: tr.id === ownTruck?.id ? 700 : 400 }}>
                        {tr.id === ownTruck?.id
                          ? t('crew.myTruckOption', { truck: tr.name })
                          : tr.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {t('crew.truckStopsToday', { count: Number(tr.today_active_jobs) || 0 })}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {viewingOtherTruck && (
          <Alert
            severity="warning"
            icon={<TruckIcon fontSize="inherit" />}
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
            role="status"
            data-demo-target="crew-viewing-other-truck"
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('crew.viewingOtherTruck', { truck: truckInfo.name, myTruck: ownTruck.name })}
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              onClick={() => handleManualTruckChange(ownTruck.id)}
              sx={BIG_TOUCH}
              aria-label={t('crew.backToMyTruckAria', { truck: ownTruck.name })}
            >
              {t('crew.backToMyTruck', { truck: ownTruck.name })}
            </Button>
          </Alert>
        )}

        {canSelectAnyTruck && truckInfo && (
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
              {t('crew.truckLocationTitle', { truck: truckInfo.name })}
            </Typography>
            {gpsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress aria-label={t('crew.gpsLoading')} />
              </Box>
            ) : (
              <>
                <MapboxMiniMap
                  latitude={selectedTruckGps?.latitude}
                  longitude={selectedTruckGps?.longitude}
                  status={
                    Number(selectedTruckGps?.speed_kph || 0) > 5
                      ? 'driving'
                      : (selectedTruckGps?.ignition_status === 'On' ? 'idle' : 'parked')
                  }
                  height="220px"
                  truckName={truckInfo.name}
                />
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1.5 }}>
                  <LocationIcon color="action" fontSize="small" />
                  <Typography variant="body2">
                    {selectedTruckAddress || t('crew.gpsNoLocation')}
                  </Typography>
                </Stack>
              </>
            )}
          </Paper>
        )}

        {/* TKT-446: crews do the day's inspection before they can see where
            they're going. Per truck, so switching trucks needs another one.
            Office roles can still look at any truck's list. */}
        {needsInspection && (
          <Alert
            severity="warning"
            icon={<InspectionIcon fontSize="inherit" />}
            sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
            data-demo-target="crew-inspection-gate"
            role="alert"
          >
            <AlertTitle sx={{ fontWeight: 700 }}>
              {t('crew.inspectionGateTitle')}
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('crew.inspectionGateBody', { truck: truckInfo?.name || '' })}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              size="large"
              startIcon={<InspectionIcon />}
              onClick={() => navigate('/dashboard/inspections/new')}
              sx={BIG_TOUCH}
            >
              {t('crew.startVehicleInspection')}
            </Button>
          </Alert>
        )}

        {!needsInspection && (<>
        {(routeCounts.snow > 0 || routeMode === 'snow') && (
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={routeMode}
            onChange={handleRouteModeChange}
            aria-label={t('crew.routeModeLabel')}
            sx={{ mb: 1.5 }}
            data-demo-target="crew-route-mode"
          >
            <ToggleButton value="summer" color="success" aria-label={t('crew.routeModeSummer', { count: routeCounts.summer })}>
              <SummerIcon fontSize="small" sx={{ mr: 0.75 }} />
              {t('crew.routeModeSummer', { count: routeCounts.summer })}
            </ToggleButton>
            <ToggleButton value="snow" color="info" aria-label={t('crew.routeModeSnow', { count: routeCounts.snow })}>
              <SnowIcon fontSize="small" sx={{ mr: 0.75 }} />
              {t('crew.routeModeSnow', { count: routeCounts.snow })}
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        <Divider textAlign="left" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="overline" color="text.secondary">
              {routeMode === 'snow'
                ? t('crew.snowListStops', { count: stops.length })
                : t('crew.todaysStops', { count: stops.length })}
            </Typography>
            {completedToday > 0 && (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<CompleteIcon />}
                label={t('crew.completedTodayCount', { count: completedToday })}
                aria-label={t('crew.completedTodayCount', { count: completedToday })}
              />
            )}
            {isAdmin && completedStops.length > 0 && (
              <Button
                size="small"
                onClick={() => setShowCompleted((v) => !v)}
                aria-expanded={showCompleted}
                data-demo-target="crew-toggle-completed"
              >
                {showCompleted ? t('crew.hideCompletedStops') : t('crew.showCompletedStops')}
              </Button>
            )}
          </Stack>
        </Divider>

        {stops.length === 0 ? (
          <Alert severity="info">
            {routeMode === 'snow' ? t('crew.noSnowStops') : t('crew.noStopsToday')}
          </Alert>
        ) : (
          stops.map(renderStopCard)
        )}

        {isAdmin && showCompleted && completedStops.length > 0 && (
          <>
            <Divider textAlign="left" sx={{ mt: 2, mb: 1 }}>
              <Typography variant="overline" color="text.secondary">
                {t('crew.completedStopsHeading', { count: completedStops.length })}
              </Typography>
            </Divider>
            {completedStops.map((stop, idx) => renderStopCard(stop, stops.length + idx))}
          </>
        )}
        </>)}
      </Box>

      <Dialog
        open={!!resetTarget}
        onClose={() => { if (!resetting) setResetTarget(null); }}
        fullWidth
        maxWidth="xs"
        aria-labelledby="crew-reset-dialog-title"
      >
        <DialogTitle id="crew-reset-dialog-title">
          {t('crew.resetStopTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('crew.resetStopBody', { title: resetTarget?.title || '' })}
          </Typography>
          {resetTarget?.is_complete && (
            <Alert severity="warning">{t('crew.resetStopEmailNote')}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)} disabled={resetting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmReset}
            disabled={resetting}
            startIcon={resetting ? <CircularProgress size={16} color="inherit" /> : <ResetIcon />}
          >
            {t('crew.resetStopConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={helpOpen}
        onClose={closeHelp}
        fullWidth
        maxWidth="sm"
        aria-labelledby="crew-help-dialog-title"
        data-demo-target="crew-help-dialog"
      >
        <DialogTitle id="crew-help-dialog-title">
          {t('crew.helpTitle')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('crew.helpHelper')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            value={helpText}
            onChange={(event) => setHelpText(event.target.value)}
            placeholder={t('crew.helpPlaceholder')}
            inputProps={{
              maxLength: 2000,
              'aria-label': t('crew.helpIssueLabel'),
            }}
            label={t('crew.helpIssueLabel')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeHelp} disabled={submittingHelp}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={submitHelpRequest}
            disabled={submittingHelp || helpText.trim() === ''}
            startIcon={submittingHelp ? <CircularProgress size={16} color="inherit" /> : <HelpIcon />}
            aria-label={t('crew.submitHelp')}
            data-demo-target="crew-submit-help"
          >
            {t('crew.submitHelp')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FB-004 — Urgent reminder confirmation dialog (gates Clock In). */}
      <Dialog
        open={reminderConfirmOpen}
        onClose={acknowledgingReminder ? undefined : closeReminderConfirm}
        fullWidth
        maxWidth="sm"
        aria-labelledby="crew-urgent-reminder-title"
        data-demo-target="crew-urgent-reminder-dialog"
      >
        <DialogTitle
          id="crew-urgent-reminder-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}
        >
          <ReminderIcon color="error" />
          {t('crew.urgentReminderTitle')}
        </DialogTitle>
        <DialogContent dividers>
          {reminderConfirmStop && (
            <Alert
              severity="error"
              icon={<ReminderIcon fontSize="inherit" />}
              sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                {reminderConfirmStop.company_name
                  || `${reminderConfirmStop.first_name || ''} ${reminderConfirmStop.last_name || ''}`.trim()
                  || t('crew.jobDetails')}
              </AlertTitle>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                {getReminderText(reminderConfirmStop)}
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('crew.urgentReminderConfirmBody')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={reminderAck}
                onChange={(e) => setReminderAck(e.target.checked)}
                inputProps={{
                  'aria-label': t('crew.urgentReminderAckLabel'),
                  'data-demo-target': 'crew-urgent-reminder-ack',
                }}
              />
            }
            label={t('crew.urgentReminderAckLabel')}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeReminderConfirm}
            disabled={acknowledgingReminder}
            aria-label={t('common.cancel')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmReminderAndClockIn}
            disabled={!reminderAck || acknowledgingReminder}
            startIcon={acknowledgingReminder
              ? <CircularProgress size={14} color="inherit" />
              : <StartIcon />}
            aria-label={t('crew.urgentReminderConfirmCta')}
            data-demo-target="crew-urgent-reminder-confirm"
          >
            {t('crew.urgentReminderConfirmCta')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FB-004 — Read-only urgent-reminder viewer (opened from the bell
          icon on the stop card). Non-gating: always shows a Close button
          regardless of acknowledgement state. */}
      <Dialog
        open={reminderViewerOpen}
        onClose={closeReminderViewer}
        fullWidth
        maxWidth="sm"
        aria-labelledby="crew-urgent-reminder-viewer-title"
        data-demo-target="crew-urgent-reminder-viewer"
      >
        <DialogTitle
          id="crew-urgent-reminder-viewer-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', pr: 6 }}
        >
          <ReminderIcon color="error" />
          {t('crew.urgentReminderTitle')}
          <IconButton
            aria-label={t('common.close')}
            onClick={closeReminderViewer}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {reminderViewerStop && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {reminderViewerStop.company_name
                  || `${reminderViewerStop.first_name || ''} ${reminderViewerStop.last_name || ''}`.trim()
                  || t('crew.jobDetails')}
              </Typography>
              <Alert
                severity="error"
                icon={<ReminderIcon fontSize="inherit" />}
                sx={{ '& .MuiAlert-message': { width: '100%' } }}
              >
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                  {getReminderText(reminderViewerStop)}
                </Typography>
              </Alert>
              {!reminderViewerStop.urgent_reminder_acknowledged && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  {t('crew.viewReminderUnackedHint')}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeReminderViewer}
            variant="contained"
            aria-label={t('common.close')}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add-note dialog */}
      <Dialog open={noteOpen} onClose={() => setNoteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('crew.addNote')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={t('crew.notePlaceholder')}
            inputProps={{ 'aria-label': t('crew.addNote') }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={saveNote}
            variant="contained"
            disabled={savingNote || !noteText.trim()}
            startIcon={savingNote ? <CircularProgress size={14} /> : null}
            aria-label={t('common.save')}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TKT-304: Clock-In uses the same Services Performed Today layout as
          the summer job log, listing only services that are on the contract. */}
      <Dialog
        open={clockInOpen}
        onClose={() => setClockInOpen(false)}
        fullWidth
        maxWidth={isWinterStop(clockInTarget) ? 'md' : 'sm'}
        scroll="paper"
        data-demo-target="crew-clockin-dialog"
      >
        <DialogTitle>{t('crew.clockInTitle')}</DialogTitle>
        <DialogContent>
          {clockInTarget && hasUrgentReminder(clockInTarget) && (
            <Alert
              severity="warning"
              icon={<ReminderIcon fontSize="inherit" />}
              sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                {t('crew.urgentReminderTitle')}
              </AlertTitle>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {getReminderText(clockInTarget)}
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isWinterStop(clockInTarget) ? t('crew.winterClockInHelper') : t('crew.clockInHelper')}
          </Typography>
          {clockInTarget && (buildCrewServiceItems(clockInTarget).length > 0 || isWinterStop(clockInTarget)) && (
            <Box sx={{ mb: 1, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <ContractedServicesChecklist
                items={buildCrewServiceItems(clockInTarget)}
                values={clockInServices}
                onToggle={toggleClockInService}
                title={t('summerLogs.servicesPerformed')}
              />
              {isWinterStop(clockInTarget) && renderWinterExtras(buildCrewServiceItems(clockInTarget).length === 0)}
            </Box>
          )}
          {clockInOpen && isWinterStop(clockInTarget) && renderWinterLog()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClockInOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={confirmClockIn}
            variant="contained"
            color="success"
            disabled={
              (clockInTarget ? !!clocking[clockInTarget.active_job_id] : false)
              || (
                buildCrewServiceItems(clockInTarget).length > 0
                && !hasTickedService(clockInTarget, clockInServices)
              )
            }
            startIcon={<StartIcon />}
            aria-label={t('jobs.clockIn')}
            data-demo-target="crew-btn-clockin-confirm"
          >
            {t('jobs.clockIn')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clock-Out & Complete dialog. Same backend call as before
          (POST /api/jobs/:id/complete-job, which sets clock_out_time AND
          is_complete=true). Wording aligns with TKT-177 / the legacy
          field workflow where clock-out is the final step. TKT-195
          promotes the per-service checklist + notes into this dialog so
          crews can't leave site without confirming what got done. */}
      <Dialog
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
        fullWidth
        maxWidth={isWinterStop(completeTarget) ? 'md' : 'sm'}
        scroll="paper"
        data-demo-target="crew-complete-dialog"
      >
        <DialogTitle>{t('crew.clockOutAndComplete')}</DialogTitle>
        <DialogContent>
          {/* FB-004 — re-surface the office's urgent reminder one last time
              before completing the job. Informational at this stage (not a
              gate) since the crew has already been clocked in for hours. */}
          {hasUrgentReminder(completeTarget) && (
            <Alert
              severity="warning"
              icon={<ReminderIcon fontSize="inherit" />}
              sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>
                {t('crew.urgentReminderTitle')}
              </AlertTitle>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {getReminderText(completeTarget)}
              </Typography>
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('crew.clockOutAndCompleteHelper')}
          </Typography>

          {/* TKT-304: same contracted Services Performed Today list as clock-in. */}
          {completeTarget && (buildCrewServiceItems(completeTarget).length > 0 || isWinterStop(completeTarget)) && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <ContractedServicesChecklist
                items={buildCrewServiceItems(completeTarget)}
                values={completeServices}
                onToggle={toggleCompleteService}
                title={t('summerLogs.servicesPerformed')}
                helper={t('crew.servicesPerformedHelper')}
              />
              {isWinterStop(completeTarget) && renderWinterExtras(buildCrewServiceItems(completeTarget).length === 0)}
              {buildCrewServiceItems(completeTarget).length > 0
                && !hasTickedService(completeTarget, completeServices) && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {t('crew.clockOutNoServicesHint')}
                </Typography>
              )}
            </Box>
          )}
          {completeOpen && isWinterStop(completeTarget) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('crew.winterClockOutHelper')}
              </Typography>
              {renderWinterLog()}
            </Box>
          )}

          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            value={completeNote}
            onChange={(e) => setCompleteNote(e.target.value)}
            label={t('crew.completionNoteOptional')}
            inputProps={{ 'aria-label': t('crew.completionNoteOptional'), 'data-demo-target': 'crew-input-complete-note' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={confirmComplete}
            variant="contained"
            color="primary"
            disabled={
              completing
              || (
                // Nothing checked: a note explaining why lets the crew clock
                // out, so a wrong or empty list can never trap them on site.
                buildCrewServiceItems(completeTarget).length > 0
                && !hasTickedService(completeTarget, completeServices)
                && completeNote.trim().length < 3
              )
            }
            startIcon={completing ? <CircularProgress size={14} /> : <CompleteIcon />}
            aria-label={t('crew.clockOutAndComplete')}
            data-demo-target="crew-btn-complete-confirm"
          >
            {t('crew.clockOutAndComplete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full job-details dialog: services performed (summer/winter) + observations */}
      <Dialog
        open={detailsOpen}
        onClose={closeJobDetails}
        fullWidth
        maxWidth="md"
        scroll="paper"
        aria-labelledby="crew-job-details-title"
        data-demo-target="crew-details-dialog"
      >
        <DialogTitle id="crew-job-details-title" sx={{ pr: 6 }}>
          {detailJob
            ? (detailJob.company_name
                || `${detailJob.first_name || ''} ${detailJob.last_name || ''}`.trim()
                || t('crew.jobDetails'))
            : t('crew.jobDetails')}
          {detailJob && (
            <Typography variant="caption" color="text.secondary" display="block">
              {[detailJob.street_address, detailJob.city].filter(Boolean).join(', ')}
            </Typography>
          )}
          <IconButton
            aria-label={t('common.close')}
            onClick={closeJobDetails}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {detailsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!detailsLoading && detailJob && (() => {
            // Decide which service blocks to show based on available_services.
            let services = detailJob.available_services;
            if (typeof services === 'string') {
              try { services = JSON.parse(services) || {}; } catch (_) { services = {}; }
            }
            services = services || {};
            // Naomi, Oct 19 2026: a winter visit logs winter work only and a
            // summer visit summer work only, even when the contract has both.
            // The services decide only when the job has no season.
            const isWinterJob = detailJob.job_type === 'winter';
            const isSummerJob = detailJob.job_type === 'summer';
            const hasSnow = isWinterJob || (!isSummerJob && !!services.snow);
            const hasSummer = isSummerJob || (!isWinterJob && Object.keys(services).some(
              (k) => k !== 'snow' && k !== 'construction'
            ));

            // FB-005 in-dialog status block, mirrors the on-card chips so
            // the same data is in both places. Computed inline here because
            // detailJob has the same field names as a stop.
            const detailAllotted = allottedMinutesFor({ ...detailJob, ...(detailAllotment || {}) });
            let detailExpected = null;
            let detailRemaining = null;
            let detailIsOver = false;
            if (detailAllotted != null && detailJob.clock_in_time) {
              detailExpected = new Date(
                new Date(detailJob.clock_in_time).getTime() + detailAllotted * 60 * 1000
              );
              const ref = detailJob.clock_out_time
                ? new Date(detailJob.clock_out_time)
                : new Date();
              detailRemaining = Math.round((detailExpected.getTime() - ref.getTime()) / 60000);
              detailIsOver = detailRemaining < 0;
            }

            return (
              <Stack spacing={3}>
                {/* FB-004 — Pinned reminder banner inside the dialog so the
                    crew can re-read it any time they're working the site. */}
                {hasUrgentReminder(detailJob) && (
                  <Alert
                    severity={detailJob.urgent_reminder_acknowledged ? 'warning' : 'error'}
                    icon={<ReminderIcon fontSize="inherit" />}
                    sx={{ '& .MuiAlert-message': { width: '100%' } }}
                  >
                    <AlertTitle sx={{ fontWeight: 700 }}>
                      {t('crew.urgentReminderTitle')}
                    </AlertTitle>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {getReminderText(detailJob)}
                    </Typography>
                  </Alert>
                )}

                {/* FB-005 — Time status block: allotted, expected complete,
                    and live remaining/over. Always shown when an allotment
                    exists so the crew can pace themselves. */}
                {detailAllotted != null && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                    aria-label={t('crew.timeStatusAria')}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <HourglassIcon color="action" fontSize="small" />
                      <Typography variant="overline" color="text.secondary">
                        {t('crew.timeStatusTitle')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        icon={<HourglassIcon />}
                        label={t('crew.allottedChip', { time: formatAllottedTime(detailAllotted) })}
                      />
                      {detailJob.clock_in_time && (
                        <Chip
                          size="small"
                          icon={<TimeIcon />}
                          label={t('crew.startedAtChip', {
                            time: formatClockTime(new Date(detailJob.clock_in_time)),
                          })}
                        />
                      )}
                      {detailExpected && !detailJob.is_complete && (
                        <Chip
                          size="small"
                          icon={<TimeIcon />}
                          label={t('crew.expectedCompleteChip', {
                            time: formatClockTime(detailExpected),
                          })}
                        />
                      )}
                      {detailRemaining != null && !detailJob.is_complete && (
                        <Chip
                          size="small"
                          color={detailIsOver ? 'error' : 'success'}
                          label={
                            detailIsOver
                              ? t('crew.minutesOver', { time: formatMinutes(Math.abs(detailRemaining)) })
                              : t('crew.minutesRemaining', { time: formatMinutes(detailRemaining) })
                          }
                        />
                      )}
                    </Stack>
                    {renderAllottedBreakdown(detailAllotment)}
                  </Box>
                )}

                {/* Property/site diagram from the originating quote, if any.
                    Crew sees a small thumbnail; tapping opens a full-screen
                    pan + pinch zoom viewer. */}
                {detailJob.site_diagram_signed_url && (
                  <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('jobs.propertyDiagram')}
                    </Typography>
                    <PropertyDiagramViewer
                      signedUrl={detailJob.site_diagram_signed_url}
                      filename={detailJob.site_diagram_filename}
                    />
                  </Box>
                )}
                {hasSummer && (
                  <SummerJobDetails job={detailJob} onUpdate={refreshDetailJob} />
                )}
                {hasSnow && (
                  <WinterJobDetails job={detailJob} onUpdate={refreshDetailJob} />
                )}

                {/* Photos — legacy TakePhoto parity. Crew can snap site
                    photos straight from the rear camera; they appear on the
                    customer's Property Live Update. */}
                <JobPhotosSection jobId={detailJob.id} />

                {/* Observations */}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <ObservationIcon color="action" />
                    <Typography variant="h6" component="h3" sx={{ m: 0 }}>
                      {t('crew.observations')}
                    </Typography>
                  </Stack>

                  {detailJob.observations && detailJob.observations.length > 0 && (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                      {detailJob.observations.map((obs) => (
                        <Card key={obs.id} variant="outlined">
                          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {obs.template_name || obs.title || t('crew.observationCustom')}
                            </Typography>
                            {obs.custom_note && (
                              <Typography variant="body2" color="text.secondary">
                                {obs.custom_note}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {obs.noted_at
                                ? new Date(obs.noted_at).toLocaleString(locale)
                                : ''}
                            </Typography>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}

                  <Stack spacing={1}>
                    {obsTemplates.length > 0 && (
                      <FormControl fullWidth size="small" data-demo-target="crew-obs-template-control">
                        <InputLabel id="obs-template-label">
                          {t('crew.observationTemplate')}
                        </InputLabel>
                        <Select
                          labelId="obs-template-label"
                          value={obsDraft.templateId}
                          label={t('crew.observationTemplate')}
                          onChange={(e) => setObsDraft((d) => ({ ...d, templateId: e.target.value }))}
                          inputProps={{ 'aria-label': t('crew.observationTemplate') }}
                          data-demo-target="crew-select-obs-template"
                        >
                          <MenuItem value="">
                            <em>{t('common.none')}</em>
                          </MenuItem>
                          {obsTemplates.map((tpl) => (
                            <MenuItem key={tpl.id} value={tpl.id}>
                              {(locale === 'es-MX' && tpl.spanish_note_name) || tpl.note_name || `#${tpl.id}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={obsDraft.note}
                      onChange={(e) => setObsDraft((d) => ({ ...d, note: e.target.value }))}
                      placeholder={t('crew.observationNotePlaceholder')}
                      inputProps={{ 'aria-label': t('crew.observations'), 'data-demo-target': 'crew-input-obs-note' }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={saveObservation}
                        disabled={savingObs || (!obsDraft.templateId && !obsDraft.note.trim())}
                        startIcon={savingObs ? <CircularProgress size={14} /> : <ObservationIcon />}
                        aria-label={t('crew.saveObservation')}
                        data-demo-target="crew-btn-obs-save"
                      >
                        {t('crew.saveObservation')}
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            );
          })()}
        </DialogContent>

        <DialogActions>
          <Button onClick={closeJobDetails} aria-label={t('common.close')} data-demo-target="crew-btn-details-close">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrewToday;
